import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitter, faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faMapMarkerAlt, faGlobe, faBuilding, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import PageContent from '../../../shared/layout/containers/PageContent';

const VenueDetails = ({ venue }) => {
    const { t } = useTranslation();
    const socialLinks = venue.social_media_links ? JSON.parse(venue.social_media_links) : {};

    return (
        <PageContent>
            <StyledCard>
                <Header>
                    <Title>{t('Venues.VenueView.VenueDetails.title')}</Title>
                </Header>
                <DetailsGrid>
                    <DetailItem>
                        <Label>
                            <FontAwesomeIcon icon={faBuilding} className="icon" />
                            {t('Venues.VenueView.VenueDetails.name_label')}
                        </Label>
                        <Value>{venue.name}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>
                            <FontAwesomeIcon icon={faEnvelope} className="icon" />
                            {t('Venues.VenueView.VenueDetails.email_label')}
                        </Label>
                        <Value>{venue.email}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>
                            <FontAwesomeIcon icon={faGlobe} className="icon" />
                            {t('Venues.VenueView.VenueDetails.website_label')}
                        </Label>
                        <Value>
                            <a href={venue.website} target="_blank" rel="noopener noreferrer" className="link">
                                {venue.website}
                            </a>
                        </Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>
                            <FontAwesomeIcon icon={faPhoneAlt} className="icon" />
                            {t('Venues.VenueView.VenueDetails.phone_label')}
                        </Label>
                        <Value>{venue.phone_country?.phonecode && `+${venue.phone_country.phonecode}`} {venue.phone}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="icon" />
                            {t('Venues.VenueView.VenueDetails.address_label')}
                        </Label>
                        <Value>{venue.address}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>{t('Venues.VenueView.VenueDetails.city_label')}</Label>
                        <Value>{venue.city}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>{t('Venues.VenueView.VenueDetails.country_label')}</Label>
                        <Value>{venue.country?.name}</Value>
                    </DetailItem>
                    <DetailItem fullWidth>
                        <Label>{t('Venues.VenueView.VenueDetails.description_label')}</Label>
                        <Value>{venue.description}</Value>
                    </DetailItem>
                </DetailsGrid>

                <Divider />

                <SocialMedia>
                    <SectionTitle>{t('Venues.VenueView.VenueDetails.social_media')}</SectionTitle>
                    <SocialRow>
                        {socialLinks.twitter && (
                            <SocialColumn>
                                <FontAwesomeIcon icon={faTwitter} className="icon twitter" />
                                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                                    {t('Venues.VenueView.VenueDetails.twitter')}
                                </a>
                            </SocialColumn>
                        )}
                        {socialLinks.facebook && (
                            <SocialColumn>
                                <FontAwesomeIcon icon={faFacebook} className="icon facebook" />
                                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                                    {t('Venues.VenueView.VenueDetails.facebook')}
                                </a>
                            </SocialColumn>
                        )}
                        {socialLinks.instagram && (
                            <SocialColumn>
                                <FontAwesomeIcon icon={faInstagram} className="icon instagram" />
                                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                                    {t('Venues.VenueView.VenueDetails.instagram')}
                                </a>
                            </SocialColumn>
                        )}
                    </SocialRow>
                </SocialMedia>
            </StyledCard>
        </PageContent>
    );
};

export default VenueDetails;

// Styled Components
const Header = styled.div`
    border-bottom: 2px solid #f1f5f9;
    padding-bottom: 1rem;
    margin-bottom: 1.5rem;
`;

const Title = styled.h3`
    color: #1e293b;
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
`;

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
`;

const DetailItem = styled.div`
    ${({ fullWidth }) => fullWidth && `grid-column: 1 / -1;`}
`;

const Label = styled.div`
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
        font-size: 0.875rem;
        color: #701a82;
    }
`;

const Value = styled.div`
    font-size: 1rem;
    color: #1e293b;
    font-weight: 500;
    line-height: 1.5;

    .link {
        color: #701a82;
        text-decoration: none;
        word-break: break-all;
        &:hover {
            text-decoration: underline;
        }
    }
`;

const Divider = styled.div`
    height: 1px;
    background: #f1f5f9;
    margin: 2rem 0;
`;

const SocialMedia = styled.div`
    margin-top: 1.5rem;
`;

const SectionTitle = styled.h4`
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 1rem;
`;

const SocialRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
`;

const SocialColumn = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .icon {
        font-size: 1.25rem;
        &.twitter { color: #1da1f2; }
        &.facebook { color: #1877f2; }
        &.instagram { color: #e4405f; }
    }

    .social-link {
        font-size: 0.95rem;
        font-weight: 500;
        color: #475569;
        text-decoration: none;
        &:hover {
            color: #701a82;
        }
    }
`;
